import "common/assets/fonts/font-awesome-4.7.0/scss/font-awesome.scss";

import { Suspense, useContext, useEffect } from "react";
import Fade from "react-reveal/Fade";
import { useLocation } from "react-router-dom";

import { Box, Stack } from "@seneca/senekit";

import loaderWithRetry from "seneca-common/utils/loaderWithRetry";

import lazyWithPreload from "features/app/components/lazyWithPreload";
import useExpandSidebarSectionEffect from "features/classroom/pages/classroom/components/Classroom/hooks/useExpandSidebarSectionEffect";
import { SidebarSkeleton } from "features/common/components/sidebar";

import SidebarHeader from "./components/SidebarHeader";
import DividerCollapse from "./components/SidebarMenu/DividerCollapse";
import SidebarMenu from "./components/SidebarMenu/SidebarMenu";
import SidebarTimer from "./components/SidebarTimer";
import Assignments from "./features/assignments";
import Conversations from "./features/chatbot/AmeliaSessions";
import CourseContentLoading from "./features/course-content/CourseContentLoading";
import { useSidebarState } from "./hooks";
import useShowAskAmelia from "./hooks/useShowAskAmelia";
import { SidebarContext } from "./SidebarContextProvider";
import { SidebarMenuItem } from "./types";

type Props = {
  courseId: string;
  activeSectionId?: string;
};

const CourseContent = lazyWithPreload(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "course-content" */
        "./features/course-content"
      )
  )
);

export default function Sidebar(props: Props) {
  const { courseId, activeSectionId } = props;

  const { opened, toggleSidebar, loadingCourse, sectionIds } =
    useSidebarState(courseId);

  const { activeItems, toggleCollapseItems, visibleItems } =
    useContext(SidebarContext);
  const noActiveItems = !Object.values(activeItems).some(Boolean);

  const menuCollapsed = Object.values(visibleItems).filter(Boolean).length <= 1;
  const onDividerClick = () => {
    const activeItem = (Object.keys(activeItems) as SidebarMenuItem[]).find(
      item => !!activeItems[item]
    )!;

    toggleCollapseItems(activeItem);
  };

  useExpandSidebarSectionEffect(courseId, activeSectionId);

  const location = useLocation<{ endTime: string } | undefined>();
  const endTime = location.state?.endTime;

  const showChatHistory = useShowAskAmelia({ gateFreeUsers: true });

  useEffect(() => {
    CourseContent.preload();
  }, []);

  return (
    <SidebarSkeleton opened={opened} lockBodyScroll>
      <SidebarHeader
        courseId={courseId}
        currentSectionId={activeSectionId}
        handleToggle={toggleSidebar}
        sidebarOpen={opened}
      />
      {loadingCourse ? (
        <CourseContentLoading />
      ) : (
        <>
          <Box paddingX="xlarge" marginBottom="large">
            <Stack space="xlarge">
              <Box
                maxHeight={!menuCollapsed ? "40rem" : "3.5rem"}
                transition="max-height 0.5s"
              >
                <SidebarMenu
                  courseId={courseId}
                  activeSectionId={activeSectionId}
                  toggleSidebar={toggleSidebar}
                />
              </Box>
              <DividerCollapse onClick={onDividerClick} />
            </Stack>
          </Box>
          {opened && endTime && (
            <Fade>
              <SidebarTimer endTime={endTime} />
            </Fade>
          )}
          <Box paddingTop="xlarge">
            <Suspense fallback={<CourseContentLoading />}>
              {(activeItems.overview || noActiveItems) && (
                <CourseContent
                  courseId={courseId}
                  sectionIds={sectionIds}
                  activeSectionId={activeSectionId!}
                />
              )}
            </Suspense>
          </Box>
          {opened && activeItems.assignments && (
            <Assignments
              courseId={courseId}
              activeSectionId={activeSectionId!}
              isSelected
            />
          )}
          {opened && activeItems.askAmelia && showChatHistory && (
            <Conversations courseId={courseId} />
          )}
        </>
      )}
    </SidebarSkeleton>
  );
}
